import { useState, useRef, useEffect } from 'react'
import { useUpdateConceptualLabelDiscoveryMutation } from '../../../../../store/apis/conceptual-label.api'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import _ from 'lodash'

export const usePeriodicSaving = (
    form, 
    patterns, 
    intervalMs = 15000, 
    getPatternDataFn
) => {
    const [isSaving, setIsSaving] = useState(false);
    const [updateConceptualLabelDiscovery] = useUpdateConceptualLabelDiscoveryMutation();
    
    // Store previous form values and patterns state for comparison
    const previousStateRef = useRef({
        formValues: {},
        patterns: []
    });
    // Track which patterns are currently being updated
    const updatingPatternsRef = useRef(new Set());
    
    // Function to save a specific pattern
    const savePattern = async (patternId) => {
        // Skip if this pattern is already being updated
        if (updatingPatternsRef.current.has(patternId)) return;
        
        try {
            updatingPatternsRef.current.add(patternId);
            const patternData = getPatternDataFn(patternId);
            if (patternData) {
                await updateConceptualLabelDiscovery(patternData);
            }
        } catch (error) {
            console.error(`Error updating pattern ${patternId}:`, error);
        } finally {
            updatingPatternsRef.current.delete(patternId);
        }
    };

    // Function to check and save all changed patterns
    const saveAllChangedPatterns = async () => {
        if (!patterns?.length) return false;
        
        const currentFormValues = form.getFieldsValue();
        const hasFormChanges = !_.isEqual(currentFormValues, previousStateRef.current.formValues);
        
        // Check for criteria changes
        const hasCriteriaChanges = !_.isEqual(
            patterns.map(p => ({ id: p.id, criteria: p.criteria })),
            previousStateRef.current.patterns.map(p => ({ id: p.id, criteria: p.criteria }))
        );
        
        if (hasFormChanges || hasCriteriaChanges) {
            setIsSaving(true);
            
            try {
                // Find which patterns have changed
                const changedPatternIds = new Set();
                
                for (const pattern of patterns) {
                    const patternId = pattern.id;
                    
                    // Check form fields
                    const fieldKeys = [
                        `name-${patternId}`,
                        `description-${patternId}`,
                        `hit-feedback-${patternId}`,
                        `miss-feedback-${patternId}`,
                        `show-hit-reasoning-${patternId}`,
                        `show-miss-reasoning-${patternId}`,
                        `color-${patternId}`,
                        `warning-level-${patternId}`,
                        `grade-${patternId}`,
                        `grouping-cap-${patternId}`,
                        `cl-${patternId}`
                    ];
                    
                    // Check if any field has changed
                    const hasChangedFields = fieldKeys.some(key => 
                        !_.isEqual(currentFormValues[key], previousStateRef.current.formValues[key])
                    );
                    
                    // Find previous pattern to compare criteria
                    const previousPattern = previousStateRef.current.patterns.find(p => p.id === patternId);
                    const hasCriteriaChanged = previousPattern && 
                        !_.isEqual(pattern.criteria, previousPattern.criteria);
                    
                    if (hasChangedFields || hasCriteriaChanged) {
                        changedPatternIds.add(patternId);
                    }
                }
                
                // Save all changed patterns
                if (changedPatternIds.size > 0) {
                    const savePromises = Array.from(changedPatternIds).map(patternId => 
                        savePattern(patternId)
                    );
                    
                    await Promise.all(savePromises);
                    
                    // Update the previous state reference
                    previousStateRef.current = {
                        formValues: currentFormValues,
                        patterns: _.cloneDeep(patterns)
                    };
                    
                    return true; // Changes were saved
                }
            } catch (error) {
                openNotificationWithIcon('error', 'Auto-save failed')
            } finally {
                setIsSaving(false);
            }
        }
        
        return false; // No changes to save
    };
    
    // Initialize previous state on mount
    useEffect(() => {
        if (patterns?.length) {
            // Add a small timeout to ensure form is fully populated
            const timeoutId = setTimeout(() => {
                previousStateRef.current = {
                    formValues: form.getFieldsValue(),
                    patterns: _.cloneDeep(patterns)
                };
            }, 300);
            
            return () => clearTimeout(timeoutId);
        }
    }, [form, patterns]);
    
    // Periodic auto-save effect
    useEffect(() => {
        const interval = setInterval(async () => {
            // Skip if we're already saving or have no patterns to save
            if (isSaving || !patterns?.length) return;
            
            await saveAllChangedPatterns();
        }, intervalMs);
        
        return () => clearInterval(interval);
    }, [form, patterns, isSaving, intervalMs, getPatternDataFn]);
    
    return { isSaving, saveAllChangedPatterns };
};